import React, { useState, useEffect } from 'react';
import ShareManagement from './ShareManagement';
import UsagePanel from './UsagePanel';
import { apiClient } from '../utils/api';

const UserSettingsModal = ({ isOpen, onClose, user, defaultTab = 'profile' }) => {
    const [activeTab, setActiveTab] = useState(defaultTab);

    useEffect(() => {
        if (isOpen && defaultTab) setActiveTab(defaultTab);
    }, [isOpen, defaultTab]);
    const [userData, setUserData] = useState(null);
    const [membershipData, setMembershipData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (isOpen && activeTab === 'profile') {
            fetchUserData();
        }
    }, [isOpen, activeTab]);

    const fetchUserData = async () => {
        try {
            setLoading(true);
            const [userResponse, membershipResponse] = await Promise.all([
                apiClient.getCurrentUser(),
                apiClient.getMembershipInfo()
            ]);

            if (userResponse?.data) {
                setUserData(userResponse.data);
            }
            if (membershipResponse?.data) {
                setMembershipData(membershipResponse.data);
            }
        } catch (error) {
            console.error('Failed to fetch user data:', error);
            // Fallback to prop user if API fails
            setUserData(user);
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    const displayUser = userData || user;

    const formatNumber = (num) => {
        if (!num) return '0';
        return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    };

    const getMembershipType = () => {
        if (membershipData?.membership?.type === 'PREMIUM') {
            return 'PROFESSIONAL';
        }
        return 'Free';
    };

    const getCreditAmount = () => {
        return membershipData?.cash?.totalNumber || 0;
    };
    
    return (
        <div
            className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 9999,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '1rem'
            }}
        >
            <div className="absolute inset-0 bg-black/50" onClick={onClose} style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.5)' }} />
            <div className="relative bg-app-bg rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] min-h-0 sm:min-h-[500px] flex flex-col sm:flex-row overflow-hidden" style={{ position: 'relative', zIndex: 1 }}>
                {/* Close Button - Mobile: Top Right Corner, Desktop: In Header */}
                <button
                    aria-label="Close"
                    onClick={onClose}
                    className="sm:hidden absolute top-3 right-3 z-10 w-8 h-8 text-gray-500 hover:text-gray-700 flex items-center justify-center text-2xl font-light"
                >
                    ×
                </button>

                {/* Sidebar Menu - Full Height */}
                <div className="sm:w-48 border-r sm:border-r border-b sm:border-b-0 border-app-border bg-app-sidebar">
                    <nav className="p-3 pr-12 sm:pr-3 flex sm:flex-col sm:space-y-1 gap-1 sm:gap-0 overflow-x-auto scroll-container sm:overflow-x-visible">
                            <button
                                onClick={() => setActiveTab('profile')}
                                className={`flex-shrink-0 sm:w-full text-left px-3 py-2 rounded-md transition-colors flex items-center text-sm ${
                                    activeTab === 'profile'
                                        ? 'bg-[#00000015] text-gray-800'
                                        : 'text-gray-700 hover:bg-[#0000000c]'
                                }`}
                            >
                                <span className="whitespace-nowrap hidden sm:inline">Thông tin cá nhân</span>
                                <span className="whitespace-nowrap sm:hidden text-sm">Cá nhân</span>
                            </button>
                            <button
                                onClick={() => setActiveTab('shares')}
                                className={`flex-shrink-0 sm:w-full text-left px-3 py-2 rounded-md transition-colors flex items-center text-sm ${
                                    activeTab === 'shares'
                                        ? 'bg-[#00000015] text-gray-800'
                                        : 'text-gray-700 hover:bg-[#0000000c]'
                                }`}
                            >
                                <span className="whitespace-nowrap hidden sm:inline">Quản lý chia sẻ</span>
                                <span className="whitespace-nowrap sm:hidden text-sm">Chia sẻ</span>
                            </button>
                            <button
                                onClick={() => setActiveTab('usage')}
                                className={`flex-shrink-0 sm:w-full text-left px-3 py-2 rounded-md transition-colors flex items-center text-sm ${
                                    activeTab === 'usage'
                                        ? 'bg-[#00000015] text-gray-800'
                                        : 'text-gray-700 hover:bg-[#0000000c]'
                                }`}
                            >
                                <span className="whitespace-nowrap hidden sm:inline">Giới hạn sử dụng</span>
                                <span className="whitespace-nowrap sm:hidden text-sm">Giới hạn</span>
                            </button>
                    </nav>
                </div>

                {/* Content Area */}
                <div className="flex-1 flex flex-col min-h-0">
                    {/* Header */}
                    <div className="px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
                        <h3 className="text-base sm:text-lg font-normal text-gray-800">
                            {activeTab === 'profile' ? 'Thông tin cá nhân' : activeTab === 'usage' ? 'Giới hạn sử dụng' : 'Quản lý chia sẻ'}
                        </h3>
                        {/* Close Button - Desktop Only */}
                        <button
                            aria-label="Close"
                            onClick={onClose}
                            className="hidden sm:flex w-6 h-6 text-gray-500 hover:text-gray-700 items-center justify-center text-2xl font-light"
                        >
                            ×
                        </button>
                    </div>

                    {/* Header Border - Inset to match content padding */}
                    <div className="border-b border-app-border mx-4 sm:mx-6" />

                    {/* Main Content */}
                    <div className="flex-1 overflow-y-auto scroll-container">
                        {activeTab === 'profile' && (
                            loading ? (
                                <div className="p-4 sm:p-6">
                                    <div className="animate-pulse space-y-4">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-4">
                                                <div className="w-14 h-14 bg-app-hover rounded-full"></div>
                                                <div className="flex-1">
                                                    <div className="h-6 bg-app-hover rounded w-32 mb-2"></div>
                                                    <div className="h-4 bg-app-hover rounded w-40"></div>
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <div className="h-8 bg-app-hover rounded w-32 mb-2"></div>
                                                <div className="h-4 bg-app-hover rounded w-24"></div>
                                            </div>
                                        </div>
                                        <div className="space-y-3">
                                            <div className="h-16 bg-app-hover rounded"></div>
                                            <div className="h-16 bg-app-hover rounded"></div>
                                            <div className="h-16 bg-app-hover rounded"></div>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
                                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                                        {/* Left side: Avatar and user info */}
                                        <div className="flex items-center gap-3 sm:gap-4">
                                            <img
                                                src={displayUser?.imageUrl || 'https://static.simplize.vn/static/fe/imgs/logo-black.png'}
                                                alt="avatar"
                                                className="w-12 h-12 sm:w-14 sm:h-14 rounded-full object-cover flex-shrink-0"
                                                onError={(e) => { e.currentTarget.src = 'https://static.simplize.vn/static/fe/imgs/logo-black.png'; }}
                                            />
                                            <div>
                                                <h3 className="text-lg sm:text-xl font-normal text-gray-800">{displayUser?.fullName || '-'}</h3>
                                                <div className="mt-1">
                                                    <span
                                                        className={`px-2 py-0.5 rounded text-xs font-medium`}
                                                        style={getMembershipType() === 'PROFESSIONAL' ? {
                                                            backgroundColor: '#ec80004c',
                                                            color: '#656f79'
                                                        } : {
                                                            backgroundColor: '#f3f4f6',
                                                            color: '#1f2937'
                                                        }}
                                                    >
                                                        {getMembershipType()}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Right side: Credit info */}
                                        <div className="text-left sm:text-right w-full sm:w-auto">
                                            <div className="mb-1">
                                                <span className="text-lg sm:text-xl font-semibold text-gray-800">{formatNumber(getCreditAmount())}</span>
                                            </div>
                                            <div className="text-xs sm:text-sm text-gray-600">Credit của bạn</div>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-1 gap-3 sm:gap-4">
                                        <div>
                                            <div className="text-xs sm:text-sm text-gray-500 mb-1">Email</div>
                                            <div className="text-xs sm:text-sm font-medium text-gray-800 p-2.5 sm:p-3 bg-app-sidebar rounded-lg break-all">{displayUser?.email || '-'}</div>
                                        </div>
                                        {displayUser?.phoneNumber && (
                                            <div>
                                                <div className="text-xs sm:text-sm text-gray-500 mb-1">Phone</div>
                                                <div className="text-xs sm:text-sm font-medium text-gray-800 p-2.5 sm:p-3 bg-app-sidebar rounded-lg">{displayUser.phoneNumber}</div>
                                            </div>
                                        )}
                                        <div>
                                            <div className="text-xs sm:text-sm text-gray-500 mb-1">Mật khẩu</div>
                                            <div className="p-2.5 sm:p-3 bg-app-sidebar rounded-lg flex items-center justify-between gap-2">
                                                <span className="text-xs sm:text-sm font-medium text-gray-800">••••••</span>
                                                <a
                                                    href="https://simplize.vn/profile/information"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-xs sm:text-sm text-brand-blue-600 hover:text-brand-blue-700 font-medium whitespace-nowrap"
                                                >
                                                    Đổi mật khẩu
                                                </a>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )
                        )}

                        {activeTab === 'shares' && <ShareManagement />}

                        {activeTab === 'usage' && <UsagePanel membershipData={membershipData} />}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UserSettingsModal;


